import { Injectable } from '@angular/core';
import {
    Cart,
    CartAWB,
    CartEvents,
    ComponentPaymentMethodName,
    PaymentCart,
    PaymentCartRequest,
    PaymentMethod,
    PaymentMethodsType,
    StationFees,
    Threshold,
} from '@cargos/sprintpay-models';
import { CartService, StorageService, StorageTypes } from '@cargos/sprintpay-services';
import dayjs from 'dayjs';
import {
    BehaviorSubject,
    Observable,
    catchError,
    defaultIfEmpty,
    distinctUntilChanged,
    finalize,
    forkJoin,
    from,
    map,
    of,
    switchMap,
    take,
    throwError,
} from 'rxjs';
import Swal from 'sweetalert2';
import { PaymentMethodsHandlerService } from '../modules/cart/lib-cart/shared/services/payment-methods-handler.service';
import { ExtraItemsService } from './extra-items.service';

export type CartGroupByFlight = PaymentCart[][];
@Injectable({
    providedIn: 'root',
})
export class CartBillService {
    private cartBill: BehaviorSubject<Cart> = new BehaviorSubject(Cart.fromJson({}));
    private submitted: BehaviorSubject<PaymentCartRequest[]> = new BehaviorSubject<PaymentCartRequest[]>([]);
    private submittedItems: PaymentCartRequest[] = [];
    private handlerEvent: BehaviorSubject<CartEvents | null> = new BehaviorSubject<CartEvents | null>(null);
    private isCartLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private cartService: CartService,
        private storageService: StorageService,
        private extraItemsService: ExtraItemsService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService
    ) {
        const submitted: PaymentCartRequest[] | null = this.storageService.getElement(
            'submittedItems',
            StorageTypes.SESSION
        );
        if (submitted !== null) {
            this.submittedItems = submitted;
            this.submitted.next(submitted);
        }
    }

    resetSubmittedItems(): void {
        this.submittedItems = [];
        this.saveSubmittedItems(null);
    }

    setAndSaveSubmittedItems(submitted: PaymentCartRequest): void {
        this.setSubmittedItems(submitted);
        this.saveSubmittedItems(this.submittedItems);
    }

    getSubmittedItems$(): Observable<PaymentCartRequest[]> {
        return this.submitted.asObservable();
    }

    private setSubmittedItems(submitted: PaymentCartRequest): void {
        this.submittedItems.push(submitted);
    }

    private saveSubmittedItems(submitted: PaymentCartRequest[] | null): void {
        this.submitted.next(submitted || []);
        this.storageService.saveElement('submittedItems', submitted, StorageTypes.SESSION);
    }

    getSubmittedItems(): PaymentCartRequest[] {
        return this.submittedItems;
    }

    setCart(cart: Cart): void {
        this.cartBill.next(cart);
    }

    getCartBill$(): Observable<Cart> {
        return this.cartBill.asObservable();
    }

    get instant_cart_bill(): Cart | null {
        return this.cartBill.value;
    }

    newEvent(event: CartEvents): void {
        this.handlerEvent.next(event);
    }

    onEvent$(): Observable<CartEvents | null> {
        return this.handlerEvent.asObservable();
    }

    setIsCartLoading(isLoading: boolean): void {
        this.isCartLoading.next(isLoading);
    }

    getIsCartLoading$(): Observable<boolean> {
        return this.isCartLoading.asObservable();
    }

    getCartRequest(
        paymentMethod?: ComponentPaymentMethodName,
        thresholdPaymentMethod?: ComponentPaymentMethodName | undefined,
        additionalFee?: number
    ): Observable<Cart> {
        return this.cartService.getCartBillV2(
            paymentMethod === 'UNKNOWN' ? undefined : paymentMethod,
            thresholdPaymentMethod,
            additionalFee
        );
    }

    /**
     * @method getCartRequestAndSetCart()
     * @param (method?: PaymentMethodsType)
     * @description Get the cart bill v2 and set the cart bill v2 value
     * @returns Observable<Cart>
     */
    getCartRequestAndSetCart(
        paymentMethod?: PaymentMethodsType | undefined,
        thresholdPaymentMethod?: PaymentMethodsType | undefined,
        additionalFee?: number
    ): Observable<Cart> {
        this.setIsCartLoading(true);
        const componentNamePaymentMethod = paymentMethod
            ? PaymentMethod.getComponentNameByType(paymentMethod)
            : undefined;
        const componentNameThresholdPaymentMethod = thresholdPaymentMethod
            ? PaymentMethod.getComponentNameByType(thresholdPaymentMethod)
            : undefined;

        return this.getCartRequest(componentNamePaymentMethod, componentNameThresholdPaymentMethod, additionalFee).pipe(
            take(1),
            map((cart: Cart) => {
                this.setCart(cart);
                this.setIsCartLoading(false);
                return cart;
            }),
            catchError((error) => {
                this.setIsCartLoading(false);
                return throwError(() => error);
            }),
            finalize(() => {
                this.setIsCartLoading(false);
            })
        );
    }

    searchAwbIntoCart(cart: PaymentCart[]): boolean {
        return !!cart.filter((charge) => !charge.hasOwnProperty('hawb')).length;
    }

    searchHawbIntoCart(cart: PaymentCart[]): boolean {
        return !!cart.filter((charge) => charge.hasOwnProperty('hawb')).length;
    }

    isThereMixes$(): Observable<boolean> {
        return this.getCart$().pipe(map((cart) => this.searchAwbIntoCart(cart) && this.searchHawbIntoCart(cart)));
    }

    searchDuplicates(cart: PaymentCart[]): boolean {
        let awbDuplicated = false;
        cart.forEach((paymentCart) => {
            const itemDuplicated = this.searchDuplicateById(cart, paymentCart);
            if (itemDuplicated) {
                awbDuplicated = true;
                return;
            }
        });
        return awbDuplicated;
    }

    searchDuplicateById(cart: PaymentCart[], paymentCart: PaymentCart): boolean {
        return !!cart.find(
            (_paymentCart) =>
                _paymentCart.id !== paymentCart.id &&
                _paymentCart.awb === paymentCart.awb &&
                ((!_paymentCart.hawb && !paymentCart.hawb) ||
                    (_paymentCart.hawb && _paymentCart.hawb === paymentCart.hawb)) &&
                paymentCart.externalData?.paymentType === _paymentCart.externalData?.paymentType &&
                paymentCart.externalData?.flightNumber === _paymentCart.externalData?.flightNumber &&
                paymentCart.externalData?.flightDate === _paymentCart.externalData?.flightDate
        );
    }

    isThereDuplicates$(): Observable<boolean> {
        return this.getCart$().pipe(map((cart) => this.searchDuplicates(cart)));
    }

    isThereDuplicatesByPayment(paymentCart: PaymentCart): Observable<boolean> {
        return this.getCart$().pipe(map((cart) => this.searchDuplicateById(cart, paymentCart)));
    }

    getCart$(): Observable<PaymentCart[]> {
        return this.getCartBill$().pipe(map((cartBill: Cart) => cartBill.getCartPaymentRequests()));
    }

    getLengthCart$(): Observable<number> {
        return this.getCart$().pipe(map((cart: PaymentCart[]) => cart.length));
    }

    isEmptyCart$(): Observable<boolean> {
        return this.getLengthCart$().pipe(map((lengthCart: number) => !lengthCart));
    }

    getCartFilterByAccessoriesAndAWB$(awb: string): Observable<PaymentCart[]> {
        return this.getCart$().pipe(
            map((cart) => {
                return cart.filter((paymentRequest: PaymentCart) => {
                    return paymentRequest.isAccessorial && paymentRequest.awb === awb;
                });
            })
        );
    }

    getPaymentCartByAmountAndTypeAndHAWBAndAWBAndFlight(
        paymentType: string,
        hawb: string,
        amount: number,
        awb: string,
        flightNumber?: string | null,
        flightDate?: string | null
    ): Observable<PaymentCart> {
        return this.getCart$().pipe(
            map((cart) => {
                return cart.filter((paymentRequest: PaymentCart) => {
                    return (
                        paymentRequest.externalData?.paymentType === paymentType &&
                        paymentRequest.hawb === hawb &&
                        paymentRequest.amount === amount &&
                        paymentRequest.awb === awb &&
                        (paymentRequest.externalData?.flightNumber || '') === (flightNumber || '') &&
                        (paymentRequest.externalData?.flightDate
                            ? dayjs(paymentRequest.externalData?.flightDate).format('YYYY-MM-DD')
                            : '') === (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                    );
                });
            }),
            map((paymentCart: PaymentCart[]) => {
                return paymentCart[0] ?? null;
            })
        );
    }

    getSubmittedByAmountAndTypeAndHAWBAndAWBAndFlight(
        paymentType: string,
        hawb: string,
        amount: number,
        awb: string,
        flightNumber?: string | null,
        flightDate?: string | null
    ): Observable<PaymentCartRequest> {
        return this.getSubmittedItems$().pipe(
            map((submitted) => {
                return submitted.filter((paymentRequest: PaymentCartRequest) => {
                    return (
                        paymentRequest.externalData?.paymentType === paymentType &&
                        paymentRequest.hawb === hawb &&
                        paymentRequest.amount === amount &&
                        paymentRequest.awb === awb &&
                        (paymentRequest.externalData?.flightNumber || '') === flightNumber &&
                        (dayjs(paymentRequest.externalData?.flightDate).format('YYYY-MM-DD') || '') ===
                            (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                    );
                });
            }),
            map((paymentCart: PaymentCartRequest[]) => {
                return paymentCart[0] ?? null;
            })
        );
    }

    getCartFilterByAccessoriesAndAWBAndFlight$(
        awb: string,
        flightNumber?: string,
        flightDate?: string
    ): Observable<PaymentCart[]> {
        return this.getCart$().pipe(
            map((cart) => {
                return cart.filter((paymentRequest: PaymentCart) => {
                    return (
                        paymentRequest.isAccessorial &&
                        paymentRequest.awb === awb &&
                        (paymentRequest.externalData?.flightNumber || '') === (flightNumber || '') &&
                        (paymentRequest.externalData?.flightDate
                            ? dayjs(paymentRequest.externalData?.flightDate).format('YYYY-MM-DD')
                            : '') === (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                    );
                });
            })
        );
    }

    getSubmittedFilterByAccessoriesAndAWBAndFlight$(
        awb: string,
        flightNumber?: string,
        flightDate?: string
    ): Observable<PaymentCartRequest[]> {
        return this.getSubmittedItems$().pipe(
            map((cart) => {
                return cart?.filter((paymentRequest: PaymentCartRequest) => {
                    return (
                        paymentRequest.isAccessorial &&
                        paymentRequest.awb === awb &&
                        (paymentRequest.externalData?.flightNumber || '') === (flightNumber || '') &&
                        (paymentRequest.externalData?.flightDate
                            ? dayjs(paymentRequest.externalData?.flightDate).format('YYYY-MM-DD')
                            : '') === (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                    );
                });
            })
        );
    }

    getPaymentCartAccessoryByPaymentTypeAndHAWBAndAWBAndFlight$(
        paymentType: string,
        hawb: string,
        awb: string,
        flightNumber?: string,
        flightDate?: string
    ): Observable<PaymentCart | undefined> {
        return this.getCartFilterByAccessoriesAndAWBAndFlight$(awb, flightNumber, flightDate).pipe(
            map((cart: PaymentCart[]) => {
                return cart.find((paymentCart: PaymentCart) => {
                    return (
                        paymentCart.externalData?.paymentType === paymentType &&
                        paymentCart.hawb === hawb &&
                        paymentCart.awb === awb &&
                        (paymentCart.externalData?.flightNumber || '') === (flightNumber || '') &&
                        (paymentCart.externalData?.flightDate
                            ? dayjs(paymentCart.externalData?.flightDate).format('YYYY-MM-DD')
                            : '') === (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                    );
                });
            })
        );
    }

    getSubmittedAccessoryByPaymentTypeAndHAWBAndAWBAndFlight$(
        paymentType: string,
        hawb: string,
        awb: string,
        flightNumber?: string,
        flightDate?: string
    ): Observable<PaymentCartRequest | undefined> {
        return this.getSubmittedFilterByAccessoriesAndAWBAndFlight$(awb, flightNumber, flightDate).pipe(
            map((cart: PaymentCartRequest[]) => {
                return cart?.find((paymentCart: PaymentCartRequest) => {
                    return (
                        paymentCart.externalData?.paymentType === paymentType &&
                        paymentCart.hawb === hawb &&
                        paymentCart.awb === awb &&
                        paymentCart.isAccessorial &&
                        (paymentCart.externalData?.flightNumber || '') === (flightNumber || '') &&
                        (paymentCart.externalData?.flightDate
                            ? dayjs(paymentCart.externalData?.flightDate).format('YYYY-MM-DD')
                            : '') === (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                    );
                });
            })
        );
    }

    getPaymentCartAccessoryByPaymentTypeAndAWBAndFlight$(
        paymentType: string,
        awb: string,
        flightNumber?: string,
        flightDate?: string
    ): Observable<PaymentCart | undefined> {
        return this.getCartFilterByAccessoriesAndAWBAndFlight$(awb, flightNumber, flightDate).pipe(
            map((cart: PaymentCart[]) => {
                return cart.find(
                    (paymentCart: PaymentCart) =>
                        paymentCart.externalData?.paymentType === paymentType &&
                        paymentCart.awb === awb &&
                        (paymentCart.externalData?.flightNumber || '') === (flightNumber || '') &&
                        (paymentCart.externalData?.flightDate
                            ? dayjs(paymentCart.externalData?.flightDate).format('YYYY-MM-DD')
                            : '') === (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                );
            })
        );
    }

    getSubmittedAccessoryByPaymentTypeAndAWBAndFlight$(
        paymentType: string,
        awb: string,
        flightNumber?: string,
        flightDate?: string
    ): Observable<PaymentCartRequest | undefined> {
        return this.getSubmittedFilterByAccessoriesAndAWBAndFlight$(awb, flightNumber, flightDate).pipe(
            map((cart: PaymentCartRequest[]) => {
                return cart?.find(
                    (paymentCart: PaymentCartRequest) =>
                        paymentCart.externalData?.paymentType === paymentType &&
                        paymentCart.awb === awb &&
                        paymentCart.isAccessorial &&
                        (paymentCart.externalData?.flightNumber || '') === (flightNumber || '') &&
                        (paymentCart.externalData?.flightDate
                            ? dayjs(paymentCart.externalData?.flightDate).format('YYYY-MM-DD')
                            : '') === (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                );
            })
        );
    }

    getPaymentCartById$(id: number): Observable<PaymentCart | undefined> {
        return this.getCart$().pipe(
            map((cart: PaymentCart[]) => cart.find((paymentCart: PaymentCart) => paymentCart.id === id))
        );
    }

    getPaymentCartByAWB$(awb: string): Observable<PaymentCart | undefined> {
        return this.getCart$().pipe(
            map((cart: PaymentCart[]) => cart.find((paymentCart: PaymentCart) => paymentCart.awb === awb))
        );
    }

    deleteCartLib(isNeededConfirmation = true): Observable<CartEvents | null> {
        if (!isNeededConfirmation) {
            return this.deleteCartActionLib();
        }

        return from(
            Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                showCancelButton: true,
                showDenyButton: false,
                showConfirmButton: true,
                confirmButtonText: 'Yes, delete it!',
                customClass: {
                    confirmButton: 'deny',
                },
                reverseButtons: true,
            })
        ).pipe(
            switchMap((result) => {
                if (!result.value) {
                    return of(null);
                }

                return this.deleteCartActionLib();
            })
        );
    }

    private deleteCartActionLib() {
        return this.cartService.deleteCart().pipe(
            map(() => {
                return CartEvents.cartDeleted;
            })
        );
    }

    deleteCartAction(emitEvent: boolean): Observable<Cart> {
        return this.cartService.deleteCart().pipe(
            map((response) => {
                this.extraItemsService.resetAccessoryAdded();
                this.setCart(Cart.fromJson({}));
                if (emitEvent) {
                    this.newEvent(CartEvents.cartDeleted);
                }
                return Cart.fromJson({});
            })
        );
    }

    deleteCart(isNeededConfirmation = true, emitEvent = true): Observable<Cart | null> {
        if (!isNeededConfirmation) {
            return this.deleteCartAction(emitEvent);
        }

        return from(
            Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                showCancelButton: true,
                showDenyButton: false,
                showConfirmButton: true,
                confirmButtonText: 'Yes, delete it!',
                customClass: {
                    confirmButton: 'deny',
                },
                reverseButtons: true,
            })
        ).pipe(
            switchMap((result) => {
                if (!result.value) {
                    return of(null);
                }

                return this.deleteCartAction(emitEvent);
            })
        );
    }

    getSubTotalCart(cart: PaymentCart[]): number {
        return cart.reduce((accumulator, currentValue) => accumulator + (currentValue.amount || 0), 0);
    }

    getSubTotalCart$(): Observable<number> {
        return this.getCart$().pipe(
            map((cart: PaymentCart[]) =>
                cart?.reduce((accumulator, currentValue) => accumulator + (currentValue.amount || 0), 0)
            )
        );
    }

    getAWBInCart(): Observable<string | undefined> {
        return this.getCart$().pipe(map((cart) => cart[0]?.awb));
    }

    getDestinationInCart(): Observable<string | undefined> {
        return this.getCart$().pipe(map((cart) => cart[0]?.destination));
    }

    getDestinationByAWB(awb: string): Observable<string | undefined> {
        return this.getPaymentCartByAWB$(awb).pipe(map((paymentCart) => paymentCart?.destination));
    }

    getAWBAndDestinationInCart(): Observable<[string | undefined, string | undefined]> {
        return forkJoin([this.getAWBInCart().pipe(take(1)), this.getDestinationInCart().pipe(take(1))]);
    }

    getAwbsInCart$(): Observable<string[]> {
        return this.getCart$().pipe(
            map((paymentCarts: PaymentCart[]) => paymentCarts.map((paymentCart: PaymentCart) => paymentCart.awb)),
            map((awbs) => awbs.filter((awb) => awb !== undefined)),
            map((awbs) => awbs as string[])
        );
    }

    getCartFilterByAWB$(awb: string, aboveThreshold?: boolean): Observable<PaymentCart[]> {
        return this.getCartPaymentRequests$(aboveThreshold).pipe(
            map((paymentRequests) =>
                paymentRequests.filter((paymentRequest: PaymentCart) => paymentRequest.awb === awb)
            )
        );
    }

    getCartFilterByAWBAndNotAccessories$(awb: string): Observable<PaymentCart[]> {
        return this.getCartFilterByAWB$(awb).pipe(
            map((paymentCarts) => {
                return paymentCarts.filter((paymentRequest: PaymentCart) => !paymentRequest.isAccessorial);
            })
        );
    }

    getAWBsFromCart$(aboveThreshold?: boolean): Observable<string[]> {
        return this.getCartPaymentRequests$(aboveThreshold).pipe(
            map((paymentRequests) => paymentRequests.map((paymentRequest: PaymentCart) => paymentRequest.awb)),
            map((awbs) => [...new Set(awbs)]),
            map((awbs) => awbs as string[])
        );
    }

    getTotalByAWB(awb: string): Observable<number> {
        return this.getCartFilterByAWB$(awb).pipe(
            map((awbs) =>
                awbs.reduce((prev, currentValue) => {
                    return prev + (currentValue.amount || 0);
                }, 0)
            )
        );
    }

    getTotalCart(): number {
        return this.cartBill.value.total;
    }

    getTotalCart$(): Observable<number> {
        return this.getCartBill$().pipe(map((cart: Cart | null) => cart?.total || 0));
    }

    getCartFilterByAWBAndFlights$(awb: string, aboveThreshold?: boolean): Observable<CartGroupByFlight> {
        return this.getCartFilterByAWB$(awb, aboveThreshold).pipe(
            map((payments) => {
                return this.groupPaymentsByKey(payments);
            })
        );
    }

    groupPaymentsByKey(payments: PaymentCart[]): CartGroupByFlight {
        const groupMap = new Map<string, PaymentCart[]>();

        payments.forEach((payment) => {
            const flightNumber = payment.externalData?.flightNumber ?? '';
            const flightDate = payment.externalData?.flightDate ?? '';
            const key = `${flightNumber}|${flightDate}`;

            const existingGroup = groupMap.get(key);
            if (existingGroup) {
                existingGroup.push(payment);
            } else {
                groupMap.set(key, [payment]);
            }
        });

        return Array.from(groupMap.values());
    }

    getCartFilterByAWB(awb: string, aboveThreshold?: boolean): PaymentCart[] {
        return this.getCartPaymentRequests(aboveThreshold).filter(
            (paymentRequest: PaymentCart) => paymentRequest.awb === awb
        );
    }

    getStationFees(): StationFees[] | undefined {
        return this.cartBill.value.stationFees;
    }

    getCartPaymentRequests(aboveThreshold?: boolean): PaymentCart[] {
        return this.cartBill.value.getCartPaymentRequests(aboveThreshold) || [];
    }

    isCartAboveThreshold$(): Observable<boolean> {
        return this.getCartPaymentRequests$().pipe(
            map((paymentRequests: PaymentCart[]) => {
                return paymentRequests.some((paymentRequest: PaymentCart) => paymentRequest.isAboveThreshold);
            })
        );
    }

    isCartBelowThreshold$(): Observable<boolean> {
        return this.getCartPaymentRequests$().pipe(
            map((paymentRequests: PaymentCart[]) => {
                return paymentRequests.some((paymentRequest: PaymentCart) => !paymentRequest.isAboveThreshold);
            })
        );
    }

    /**
     * @method getCartV2$()
     * @description This method retrieves the items in the cart
     * @returns Observable<PaymentCart[]> - The items in the cart.
     */
    getTotalByThreshold$(aboveThreshold?: boolean): Observable<number> {
        return this.getCartPaymentRequests$(aboveThreshold).pipe(
            switchMap((paymentsCart: PaymentCart[]) => {
                return forkJoin(paymentsCart.map((paymentCart: PaymentCart) => of(paymentCart.amount || 0))).pipe(
                    defaultIfEmpty([])
                );
            }),
            map((allAmounts) => allAmounts?.reduce((acc, item) => acc + item, 0))
        );
    }

    getCartPaymentRequests$(aboveThreshold?: boolean): Observable<PaymentCart[]> {
        return this.getPaymentRequestsGroupByAWB$().pipe(
            map((paymentRequests: CartAWB[]) => {
                if (paymentRequests) {
                    return paymentRequests.map((cartBillResponse: CartAWB) => cartBillResponse.paymentRequest).flat();
                }

                return [];
            }),
            map((paymentRequests) => {
                return paymentRequests.filter((paymentRequest: PaymentCart) => {
                    if (aboveThreshold === true) {
                        return paymentRequest.isAboveThreshold;
                    }
                    if (aboveThreshold === false) {
                        return !paymentRequest.isAboveThreshold;
                    }

                    return true;
                });
            })
        );
    }

    getPaymentRequestsGroupByAWB$(): Observable<CartAWB[]> {
        return this.getCartBill$().pipe(
            map((cart: Cart | null) => {
                if (cart) {
                    return cart?.cartBillResponseDTOList;
                }

                return [];
            })
        );
    }

    getAWBInfoById(awb: string, id: number): CartAWB | undefined {
        if (!this.instant_cart_bill) {
            return undefined;
        }
        return this.instant_cart_bill?.cartBillResponseDTOList.find((cartAWB: CartAWB) => {
            return (
                cartAWB.awb === awb && cartAWB.paymentRequest.some((paymentCart: PaymentCart) => paymentCart.id === id)
            );
        });
    }

    getAWBInfoByAWB(awb: string, aboveThreshold?: boolean): Observable<CartAWB[]> {
        return this.getPaymentRequestsGroupByAWB$().pipe(
            map((cartAWBList: CartAWB[]) => {
                const matchingCartAWBs = cartAWBList.filter((cart) => cart.awb === awb);

                if (!matchingCartAWBs.length) {
                    return [];
                }
                let resultCartAWBs: CartAWB[] = [];

                matchingCartAWBs.forEach((cartAWB) => {
                    const filteredPaymentRequests = cartAWB.paymentRequest.filter((paymentRequest: PaymentCart) => {
                        if (aboveThreshold === true) {
                            return paymentRequest.isAboveThreshold;
                        }
                        if (aboveThreshold === false) {
                            return !paymentRequest.isAboveThreshold;
                        }
                        return true;
                    });
                    if (filteredPaymentRequests.length > 0) {
                        resultCartAWBs.push({
                            ...cartAWB,
                            paymentRequest: filteredPaymentRequests,
                        });
                    }
                });

                return resultCartAWBs;
            })
        );
    }

    isCartAboveThreshold(): boolean {
        return this.getCartPaymentRequests().some((paymentRequest: PaymentCart) => paymentRequest.isAboveThreshold);
    }

    isCartBelowThreshold(): boolean {
        return this.getCartPaymentRequests().some((paymentRequest: PaymentCart) => !paymentRequest.isAboveThreshold);
    }

    isTherePaymentsRequestsNoInvoices$(): Observable<boolean> {
        return this.getPaymentsRequestsNoInvoices$().pipe(
            map((paymentCarts) => !!paymentCarts.length),
            distinctUntilChanged()
        );
    }

    getPaymentsRequestsNoInvoices$(): Observable<PaymentCart[]> {
        return this.getCartPaymentRequests$().pipe(
            map((paymentCarts) => {
                return paymentCarts.filter((paymentCart: PaymentCart) => !paymentCart.originalInvoice);
            })
        );
    }

    getSubtotal(): number {
        if (!this.instant_cart_bill?.subTotal) {
            return this.calculateSubtotal();
        }
        return this.instant_cart_bill?.subTotal || 0;
    }

    private calculateSubtotal(): number {
        return this.getCartPaymentRequests().reduce((acc: number, paymentCart: PaymentCart) => {
            return acc + (paymentCart.amount || 0);
        }, 0);
    }

    isThereInvoicesInCart(): boolean {
        return !!this.instant_cart_bill?.isThereInvoicesInCart();
    }

    isTherePaymentsRequests(): boolean {
        return !this.instant_cart_bill?.isCartEmpty() && !this.instant_cart_bill?.isThereInvoicesInCart();
    }

    getSPFRateByThreshold(aboveThreshold?: boolean): Observable<number | null> {
        return this.getCartBill$().pipe(
            map((cartBill: Cart | null) => {
                if (!cartBill) return null;

                if (aboveThreshold) {
                    return cartBill.threshold?.lendingRate || null;
                }

                return cartBill.lendingRate || null;
            })
        );
    }

    getSubTotalByThreshold(aboveThreshold: boolean): Observable<number> {
        return this.getCartBill$().pipe(
            map((cartBill: Cart | null) => {
                return aboveThreshold ? cartBill?.threshold?.subTotal || 0 : cartBill?.subTotal || 0;
            })
        );
    }

    getLendingTerms$(aboveThreshold: boolean): Observable<number> {
        return this.getCartBill$().pipe(
            map((cartBill: Cart | null) => {
                return aboveThreshold ? cartBill?.threshold?.lendingTerms || 0 : cartBill?.lendingTerms || 0;
            })
        );
    }

    getMessageThreshold(): Observable<Threshold | null> {
        return this.getCartBill$().pipe(
            map((cartBill: Cart | null) => {
                return cartBill?.threshold || null;
            })
        );
    }
}
